import React, { useState } from "react";
import { useAuth } from "react-oidc-context";
import { useUserPreferences } from "../contexts/UserPreferencesContext";

const normalizeText = (text) => {
  const polishChars = {
    ą: "a",
    ć: "c",
    ę: "e",
    ł: "l",
    ń: "n",
    ó: "o",
    ś: "s",
    ź: "z",
    ż: "z",
    Ą: "A",
    Ć: "C",
    Ę: "E",
    Ł: "L",
    Ń: "N",
    Ó: "O",
    Ś: "S",
    Ź: "Z",
    Ż: "Z",
  };

  return text
    .toLowerCase()
    .split("")
    .map((char) => polishChars[char] || char)
    .join("")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/_+/g, "_");
};

const ProgressBar = ({ progress, total }) => {
  const percentage = Math.round((progress / total) * 100);
  const { theme } = useUserPreferences();
  const isDark = theme === "dark";

  return (
    <div className="relative pt-1 w-full">
      <div className="flex mb-2 items-center justify-between">
        <div>
          <span
            className={`text-xs font-semibold inline-block ${
              isDark ? "text-blue-300" : "text-blue-600"
            }`}
          >
            Importing Users...
          </span>
        </div>
        <div className="text-right">
          <span
            className={`text-xs font-semibold inline-block ${
              isDark ? "text-blue-300" : "text-blue-600"
            }`}
          >
            {progress} of {total} ({percentage}%)
          </span>
        </div>
      </div>
      <div
        className={`overflow-hidden h-2 text-xs flex rounded ${
          isDark ? "bg-blue-900" : "bg-blue-200"
        }`}
      >
        <div
          style={{ width: `${percentage}%` }}
          className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-500 transition-all duration-300"
        />
      </div>
    </div>
  );
};

const CSVImport = ({ onUsersCreated, fetchUsers, translations: t }) => {
  const [csvContent, setCsvContent] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [importProgress, setImportProgress] = useState(0);
  const [error, setError] = useState(null);
  const auth = useAuth();
  const { theme } = useUserPreferences();
  const isDark = theme === "dark";

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    setError(null);

    if (!file) return;

    if (!file.name.endsWith(".csv")) {
      setError("Please upload a CSV file");
      event.target.value = null;
      return;
    }

    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const lines = text
          .split("\n")
          .map((line) => line.trim())
          .filter((line) => line) // Remove empty lines
          .map((line, index) => {
            if (
              index === 0 &&
              line.toLowerCase().includes("username") &&
              line.toLowerCase().includes("email")
            ) {
              return null; // Skip header row
            }
            const [username, email] = line
              .split(";")
              .map((item) => item.trim());
            if (!username || !email) return null;

            // Najpierw normalizacja
            const normalizedUsername = normalizeText(username);
            const [localPart, domain] = email.split("@");
            const normalizedEmail = `${normalizeText(
              localPart
            )}@${domain.toLowerCase()}`;

            return {
              username: normalizedUsername,
              email: normalizedEmail,
              originalUsername: username,
              originalEmail: email,
            };
          })
          .filter((user) => user !== null);

        if (lines.length === 0) {
          setError("No valid user data found in the CSV file");
          event.target.value = null;
          return;
        }

        // Validate email format
        const invalidEmails = lines.filter(
          (user) => !user.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)
        );
        if (invalidEmails.length > 0) {
          setError(
            `Invalid email format found for ${invalidEmails.length} users`
          );
          event.target.value = null;
          return;
        }

        // Validate username format AFTER normalization
        const invalidUsernames = lines.filter(
          (user) => !user.username.match(/^[a-z0-9_]+$/)
        );
        if (invalidUsernames.length > 0) {
          const errorDetails = invalidUsernames
            .map(
              (u) =>
                `- Oryginalna nazwa: "${u.originalUsername}" -> Po normalizacji: "${u.username}"`
            )
            .join("\n");

          setError(
            `Znaleziono ${invalidUsernames.length} nieprawidłowych nazw użytkowników:\n${errorDetails}\n` +
              "Dozwolone są tylko litery, cyfry i podkreślnik (_)"
          );
          console.log("Nieprawidłowe nazwy użytkowników:", invalidUsernames);
          event.target.value = null;
          return;
        }

        setCsvContent(lines);
        setShowPreview(true);
        setImportResults(null);
      } catch (error) {
        console.error("Error parsing CSV:", error);
        setError("Error parsing CSV file. Please check the format.");
        event.target.value = null;
      }
    };

    reader.onerror = () => {
      setError("Error reading the file");
      event.target.value = null;
    };

    reader.readAsText(file);
  };

  const handleConfirmImport = async () => {
    if (
      !window.confirm(
        `Are you sure you want to create ${csvContent.length} new users?`
      )
    ) {
      return;
    }

    setIsImporting(true);
    setImportProgress(0);
    setError(null);
    const successfulUsers = [];
    const failedUsers = [];

    try {
      const token = auth.user?.access_token;
      if (!token) {
        throw new Error("No access token available");
      }

      for (let i = 0; i < csvContent.length; i++) {
        const userData = csvContent[i];
        try {
          const response = await fetch(
            "https://x0mhcopby7.execute-api.eu-central-1.amazonaws.com/UserManagementFunction",
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                username: userData.username,
                email: userData.email,
                password: `Welcome1!${new Date().getFullYear()}`,
                group: "Users",
                quota: 100,
                assignedModels: [],
              }),
            }
          );

          const responseData = await response.json();

          if (!response.ok) {
            let errorMessage =
              responseData.message || `HTTP error! status: ${response.status}`;
            if (
              responseData.code === "UsernameExistsException" ||
              errorMessage.includes("UsernameExistsException")
            ) {
              errorMessage = "Username already exists in AWS";
            }
            throw new Error(errorMessage);
          }

          successfulUsers.push(userData);
        } catch (error) {
          failedUsers.push({
            user: userData,
            error: error.message,
            isConflict: error.message.includes("already exists"),
          });
        }
        setImportProgress(i + 1);
      }

      if (successfulUsers.length > 0) {
        await fetchUsers();
        onUsersCreated?.();
      }

      const conflicts = failedUsers.filter((f) => f.isConflict);
      const otherErrors = failedUsers.filter((f) => !f.isConflict);

      setImportResults({
        successfulUsers,
        conflicts,
        otherErrors,
        totalAttempted: csvContent.length,
      });
    } catch (error) {
      console.error("Import failed:", error);
      setError("General import failure: " + error.message);
      setImportResults({
        successfulUsers: [],
        conflicts: [],
        otherErrors: csvContent.map((user) => ({
          user,
          error: error.message || "General import failure",
          isConflict: false,
        })),
        totalAttempted: csvContent.length,
      });
    } finally {
      setIsImporting(false);
      setShowPreview(false);
      setCsvContent([]);
      setImportProgress(0);
    }
  };

  return (
    <div
      className={`mt-6 p-4 border rounded-lg ${
        isDark ? "bg-gray-700 border-gray-600" : "bg-gray-50 border-gray-200"
      }`}
    >
      <h3
        className={`text-lg font-medium mb-4 ${
          isDark ? "text-white" : "text-gray-900"
        }`}
      >
        {t.importUsers}
      </h3>

      {error && (
        <div
          className={`mb-4 p-3 rounded ${
            isDark
              ? "bg-red-900/50 text-red-200 border border-red-700"
              : "bg-red-50 border border-red-200 text-red-700"
          }`}
        >
          <pre className="whitespace-pre-wrap">{error}</pre>
        </div>
      )}

      {isImporting && (
        <div className="mb-4">
          <ProgressBar progress={importProgress} total={csvContent.length} />
        </div>
      )}

      {importResults && (
        <div className="mb-6">
          <div
            className={`p-4 rounded-lg ${
              importResults.successfulUsers.length === 0
                ? isDark
                  ? "bg-red-900/50 border border-red-700"
                  : "bg-red-50 border border-red-200"
                : importResults.conflicts.length > 0 ||
                  importResults.otherErrors.length > 0
                ? isDark
                  ? "bg-yellow-900/50 border border-yellow-700"
                  : "bg-yellow-50 border border-yellow-200"
                : isDark
                ? "bg-green-900/50 border border-green-700"
                : "bg-green-50 border border-green-200"
            }`}
          >
            <h4
              className={`font-medium mb-2 ${
                isDark ? "text-white" : "text-gray-900"
              }`}
            >
              Import Results
            </h4>

            <p
              className={
                importResults.successfulUsers.length > 0
                  ? "text-green-500"
                  : isDark
                  ? "text-gray-300"
                  : "text-gray-700"
              }
            >
              Successfully created {importResults.successfulUsers.length} out of{" "}
              {importResults.totalAttempted} users
            </p>

            {importResults.conflicts.length > 0 && (
              <div className="mt-4">
                <p
                  className={
                    isDark
                      ? "text-orange-300 font-medium"
                      : "text-orange-700 font-medium"
                  }
                >
                  Existing Usernames ({importResults.conflicts.length}):
                </p>
                <div className="mt-2 max-h-40 overflow-y-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr>
                        <th
                          className={`text-left py-2 ${
                            isDark ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Username
                        </th>
                        <th
                          className={`text-left py-2 ${
                            isDark ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Email
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {importResults.conflicts.map((failure, index) => (
                        <tr
                          key={index}
                          className={`border-t ${
                            isDark ? "border-orange-800" : "border-orange-100"
                          }`}
                        >
                          <td
                            className={`py-2 ${
                              isDark ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            {failure.user.username}
                          </td>
                          <td
                            className={`py-2 ${
                              isDark ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            {failure.user.email}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {importResults.otherErrors.length > 0 && (
              <div className="mt-4">
                <p
                  className={
                    isDark
                      ? "text-red-300 font-medium"
                      : "text-red-700 font-medium"
                  }
                >
                  Other Errors ({importResults.otherErrors.length}):
                </p>
                <div className="mt-2 max-h-40 overflow-y-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr>
                        <th
                          className={`text-left py-2 ${
                            isDark ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Username
                        </th>
                        <th
                          className={`text-left py-2 ${
                            isDark ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Email
                        </th>
                        <th
                          className={`text-left py-2 ${
                            isDark ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Error
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {importResults.otherErrors.map((failure, index) => (
                        <tr
                          key={index}
                          className={`border-t ${
                            isDark ? "border-red-800" : "border-red-100"
                          }`}
                        >
                          <td
                            className={`py-2 ${
                              isDark ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            {failure.user.username}
                          </td>
                          <td
                            className={`py-2 ${
                              isDark ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            {failure.user.email}
                          </td>
                          <td
                            className={`py-2 ${
                              isDark ? "text-red-300" : "text-red-600"
                            }`}
                          >
                            {failure.error}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="mb-4">
        <input
          type="file"
          accept=".csv,text/csv"
          onChange={handleFileUpload}
          disabled={isImporting}
          className={`block w-full text-sm ${
            isDark
              ? "text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 " +
                "file:text-sm file:font-semibold file:bg-blue-900 file:text-blue-300 hover:file:bg-blue-800"
              : "text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 " +
                "file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          }`}
          placeholder={t.selectFile}
        />
        <p
          className={`mt-1 text-sm ${
            isDark ? "text-gray-400" : "text-gray-500"
          }`}
        >
          {t.csvFormat}
        </p>
      </div>

      {showPreview && csvContent.length > 0 && (
        <div className="mt-4">
          <h4
            className={`font-medium mb-2 ${
              isDark ? "text-white" : "text-gray-900"
            }`}
          >
            Preview ({csvContent.length} users):
          </h4>
          <div
            className={`max-h-60 overflow-y-auto border rounded ${
              isDark
                ? "bg-gray-800 border-gray-600"
                : "bg-white border-gray-200"
            }`}
          >
            <table className="min-w-full divide-y divide-gray-200">
              <thead className={isDark ? "bg-gray-700" : "bg-gray-50"}>
                <tr>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium uppercase ${
                      isDark ? "text-gray-300" : "text-gray-500"
                    }`}
                  >
                    Original Username
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium uppercase ${
                      isDark ? "text-gray-300" : "text-gray-500"
                    }`}
                  >
                    Normalized Username
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium uppercase ${
                      isDark ? "text-gray-300" : "text-gray-500"
                    }`}
                  >
                    Original Email
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium uppercase ${
                      isDark ? "text-gray-300" : "text-gray-500"
                    }`}
                  >
                    Normalized Email
                  </th>
                </tr>
              </thead>
              <tbody
                className={`divide-y ${
                  isDark ? "divide-gray-700" : "divide-gray-200"
                }`}
              >
                {csvContent.map((user, index) => (
                  <tr
                    key={index}
                    className={isDark ? "bg-gray-800" : "bg-white"}
                  >
                    <td
                      className={`px-6 py-4 whitespace-nowrap text-sm ${
                        isDark ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      {user.originalUsername}
                    </td>
                    <td
                      className={`px-6 py-4 whitespace-nowrap text-sm ${
                        isDark ? "text-gray-300" : "text-gray-900"
                      }`}
                    >
                      {user.username}
                    </td>
                    <td
                      className={`px-6 py-4 whitespace-nowrap text-sm ${
                        isDark ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      {user.originalEmail}
                    </td>
                    <td
                      className={`px-6 py-4 whitespace-nowrap text-sm ${
                        isDark ? "text-gray-300" : "text-gray-900"
                      }`}
                    >
                      {user.email}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex justify-end space-x-3">
            <button
              onClick={() => {
                setShowPreview(false);
                setImportResults(null);
              }}
              className={`px-4 py-2 border rounded ${
                isDark
                  ? "border-gray-600 text-gray-300 hover:bg-gray-600"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
              disabled={isImporting}
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmImport}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              disabled={isImporting}
            >
              {isImporting
                ? "Importing..."
                : `Import ${csvContent.length} Users`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CSVImport;
